import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

// 뷰포트 교차 임계값 (active/inactive 판단 기준)
const MEDIA_VISIBILITY_THRESHOLD = 0;

/**
 * 지연 로딩 미디어의 공용 뷰포트 옵저버.
 * 뷰포트를 벗어나면 일시정지, 재진입하면 이전 재생 상태로 복원한다.
 */
class MediaViewportObserver {
  /** @type {MediaViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * 관찰 대상 요소별로 그룹화된 미디어 요소들
   * @type {Map<Element, Set<DeferredMedia>>}
   */
  #mediaByTarget = new Map();

  /**
   * 싱글턴 인스턴스를 가져온다
   * @returns {MediaViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new MediaViewportObserver();
    }
    return this.#instance;
  }

  /**
   * 관찰 대상 요소를 선택한다. swiper 내부 미디어는 bounding rect가 슬라이드 transform으로 움직여서
   * 미디어 요소 자체가 아니라 swiper 컨테이너를 관찰한다.
   * @param {DeferredMedia} media
   * @returns {Element}
   */
  #getObservationTarget(media) {
    return media.closest('.swiper') ?? media;
  }

  /**
   * 지연 로딩 미디어 요소를 뷰포트 관찰 대상으로 등록한다
   * @param {DeferredMedia} media - 관찰할 지연 로딩 미디어 요소
   */
  observe(media) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const mediaElements = this.#mediaByTarget.get(entry.target);
            if (!mediaElements) continue;

            for (const media of mediaElements) {
              if (entry.isIntersecting) {
                media.handleMediaActive();
              } else {
                media.handleMediaInactive();
              }
            }
          }
        },
        { threshold: MEDIA_VISIBILITY_THRESHOLD }
      );
    }

    const target = this.#getObservationTarget(media);
    let mediaElements = this.#mediaByTarget.get(target);
    if (!mediaElements) {
      mediaElements = new Set();
      this.#mediaByTarget.set(target, mediaElements);
      this.#observer.observe(target);
    }
    mediaElements.add(media);
  }

  /**
   * 지연 로딩 미디어 요소의 관찰을 중지한다
   * @param {DeferredMedia} media - 관찰을 중지할 지연 로딩 미디어 요소
   */
  unobserve(media) {
    const target = this.#getObservationTarget(media);
    const mediaElements = this.#mediaByTarget.get(target);
    if (!mediaElements) return;

    mediaElements.delete(media);
    if (mediaElements.size === 0) {
      this.#observer?.unobserve(target);
      this.#mediaByTarget.delete(target);
    }
  }
}

/**
 * 지연 로딩 미디어 요소
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - 지연 로딩 미디어 콘텐츠를 보여주는 버튼
 * @property {HTMLElement} toggleMediaButton - 미디어를 토글하는 버튼
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** 뷰포트 이탈 직전 재생 여부 (재진입 시 복원용) */
  #wasPlayingBeforeInactive = false;

  /** 현재 뷰포트 내에 있는지 여부 */
  #isInViewport = true;

  /**
   * 캐러셀(swiper) 슬라이드로 현재 보여지고 있는지 여부. 캐러셀 밖이면 항상 true.
   * swiper 슬라이드는 connectedCallback()이 swiper 초기화보다 먼저 실행되므로 일단 false로 시작한다.
   */
  #isSlideVisible = true;

  /**
   * 자동재생 콘텐츠의 재생 상태 추적을 이미 초기화했는지 여부.
   * Swiper 복제 슬라이드는 attribute는 복사되지만 private 필드는 복사되지 않으므로 필드로 관리한다.
   */
  #autoplaySynced = false;

  /** bp-mobile-only/bp-tablet-pc-only 동기화 파트너에게 상태 전달 시 무한 재귀 방지 가드 */
  #isApplyingBreakpointSync = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.closest('.swiper')) {
      this.#isSlideVisible = false;
    }

    const signal = this.#abortController.signal;
    document.addEventListener(
      ThemeEvents.mediaStartedPlaying,
      (event) => {
        // 브레이크포인트 파트너 로드 시 발생하는 이벤트는 무시 (동기화 원본까지 일시정지되는 것 방지)
        if (/** @type {any} */ (event).detail?.resource === this.#getBreakpointSyncPartner()) return;
        this.pauseMedia();
      },
      { signal }
    );
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
    MediaViewportObserver.getInstance().observe(this);

    // Liquid가 직접 렌더링한 자동재생 video/iframe은 loadContent()를 거치지 않으므로 여기서 초기화한다.
    if (this.hasAttribute('autoplay') && !this.#autoplaySynced) {
      const hasRenderedMedia = this.querySelector('video, iframe[data-video-type]');
      if (hasRenderedMedia instanceof HTMLVideoElement || hasRenderedMedia instanceof HTMLIFrameElement) {
        this.#autoplaySynced = true;
        this.setAttribute('data-media-loaded', 'true');
        this.isPlaying = true;
        this.#wasPlayingBeforeInactive = true;
        this.#syncRealPlaybackState(hasRenderedMedia);
      }
    }
  }

  /**
   * `isPlaying`을 실제 재생 상태와 동기화한다. 네이티브 컨트롤/임베드 자체 컨트롤은 playMedia()/
   * pauseMedia()를 거치지 않을 수 있으므로 실제 이벤트를 리스닝한다.
   * @param {HTMLVideoElement | HTMLIFrameElement} mediaElement
   */
  #syncRealPlaybackState(mediaElement) {
    const signal = this.#abortController.signal;

    if (mediaElement instanceof HTMLVideoElement) {
      mediaElement.addEventListener(
        'play',
        () => {
          this.#setPlaybackState(true);
        },
        { signal }
      );
      mediaElement.addEventListener(
        'pause',
        () => {
          this.#setPlaybackState(false);
        },
        { signal }
      );
      return;
    }

    if (!(mediaElement instanceof HTMLIFrameElement) || !mediaElement.dataset.videoType) return;

    const videoType = mediaElement.dataset.videoType;

    // 임베드가 실제 상태를 보고하도록 구독 메시지를 보낸다 (즉시 1회 + 'load' 시점에 재시도)
    const subscribe = () => {
      if (videoType === 'vimeo') {
        // Vimeo는 명시적 구독 후에만 재생/일시정지 상태를 보고한다.
        mediaElement.contentWindow?.postMessage('{"method":"addEventListener","value":"play"}', '*');
        mediaElement.contentWindow?.postMessage('{"method":"addEventListener","value":"pause"}', '*');
      } else if (videoType === 'youtube') {
        // YouTube는 이 핸드셰이크 없이는 명령과 상태 업데이트를 모두 무시한다.
        mediaElement.contentWindow?.postMessage('{"event":"listening","id":1,"channel":"widget"}', '*');
      }
    };

    subscribe();
    mediaElement.addEventListener('load', subscribe, { signal });

    window.addEventListener(
      'message',
      (event) => {
        if (event.source !== mediaElement.contentWindow) return;

        /** @type {any} */
        let data;
        try {
          data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        } catch {
          return;
        }

        if (videoType === 'youtube' && data.event === 'infoDelivery' && typeof data.info?.playerState === 'number') {
          const state = data.info.playerState;
          if (state === 1) this.#setPlaybackState(true);
          else if (state === 0 || state === 2) this.#setPlaybackState(false);
        } else if (videoType === 'vimeo' && (data.event === 'play' || data.event === 'pause')) {
          this.#setPlaybackState(data.event === 'play');
        }
      },
      { signal }
    );
  }

  /**
   * 재생 상태와, 재진입 시 복원할 메모리 값을 갱신한다.
   * @param {boolean} isPlaying
   */
  #setPlaybackState(isPlaying) {
    this.isPlaying = isPlaying;

    // 완전히 보이는 상태에서의 진짜 상태 변화만 반영 (inactive 처리 중 비동기 확인 응답이 덮어쓰는 것 방지)
    if (this.#isInViewport && this.#isSlideVisible) {
      this.#wasPlayingBeforeInactive = isPlaying;
      this.#syncBreakpointPartnerPlayback(isPlaying);
    }

    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(isPlaying);
    }
  }

  /**
   * 같은 부모 안에서 bp-mobile-only/bp-tablet-pc-only가 반대인 형제 <deferred-media>(브레이크포인트
   * 동기화 파트너)를 찾는다. 없으면 null.
   * @returns {DeferredMedia | null}
   */
  #getBreakpointSyncPartner() {
    const isMobileOnly = this.classList.contains('bp-mobile-only');
    const isTabletPcOnly = this.classList.contains('bp-tablet-pc-only');
    if (!isMobileOnly && !isTabletPcOnly) return null;

    const partnerClass = isMobileOnly ? 'bp-tablet-pc-only' : 'bp-mobile-only';
    const parent = this.parentElement;
    if (!parent) return null;

    for (const sibling of parent.children) {
      if (sibling !== this && sibling instanceof DeferredMedia && sibling.classList.contains(partnerClass)) {
        return sibling;
      }
    }
    return null;
  }

  /**
   * 브레이크포인트 동기화 파트너(모바일/데스크톱 쌍)에게 재생/일시정지 상태를 전달해 둘이 항상
   * 일치하도록 한다. 파트너가 숨겨진 쪽이면(CSS display:none) 실제 재생은 시키지 않고 재개할 상태만
   * 기억해둔다 - IntersectionObserver는 display:none 요소의 교차 비율 변화를 감지하지 못해서
   * 강제로 재생시키면 화면 밖에서 영원히 재생 중인 채로 남기 때문이다.
   * @param {boolean} isPlaying
   */
  #syncBreakpointPartnerPlayback(isPlaying) {
    if (this.#isApplyingBreakpointSync) return;

    const partner = this.#getBreakpointSyncPartner();
    if (!partner) return;

    partner.#wasPlayingBeforeInactive = isPlaying;

    const partnerIsVisible = partner.#isInViewport && partner.#isSlideVisible;
    const partnerWasLoaded = !!partner.getAttribute('data-media-loaded');

    partner.#isApplyingBreakpointSync = true;
    try {
      if (isPlaying && !partnerWasLoaded) {
        // 숨겨져 있어도 미리 로드해둬서, 나중에 보이는 쪽이 됐을 때 즉시 재개 가능하게 만든다.
        partner.loadContent(false);
      }

      if (!partnerIsVisible) {
        // 숨겨진 쪽은 일시정지 상태 유지. #activate()가 나중에 보이면 자동으로 재개시킨다.
        if (partner.isPlaying || (isPlaying && !partnerWasLoaded)) {
          partner.pauseMedia();
        }
      } else if (isPlaying !== partner.isPlaying) {
        if (isPlaying) {
          partner.playMedia();
        } else {
          partner.pauseMedia();
        }
      }
    } finally {
      partner.#isApplyingBreakpointSync = false;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    MediaViewportObserver.getInstance().unobserve(this);
  }

  /** 뷰포트 이탈 시 옵저버가 호출. 재생 여부를 기억한 뒤 일시정지 */
  handleMediaInactive() {
    this.#isInViewport = false;
    this.#deactivate();
  }

  /** 뷰포트 재진입 시 옵저버가 호출. 이전 재생 상태로 복원 */
  handleMediaActive() {
    this.#isInViewport = true;
    this.#activate();
  }

  /** 슬라이드가 안 보이게 될 때 캐러셀이 호출. 재생 여부를 기억한 뒤 일시정지 */
  handleSlideHidden() {
    this.#isSlideVisible = false;
    this.#deactivate();
  }

  /** 슬라이드가 다시 보이게 될 때 캐러셀이 호출. 이전 재생 상태로 복원 */
  handleSlideVisible() {
    this.#isSlideVisible = true;
    this.#activate();
  }

  /** 재생 중이었다면 일시정지시키고, 완전히 보이는 상태가 되면 #activate()가 복원하도록 기억해둔다 */
  #deactivate() {
    if (!this.getAttribute('data-media-loaded')) return;

    // 중복 호출로 인해 잘못된 값(false)이 덮어쓰지 않도록 진짜 재생->일시정지 전환일 때만 기록한다.
    if (this.isPlaying) {
      this.#wasPlayingBeforeInactive = true;
      this.pauseMedia();
    }
  }

  /** inactive 직전 재생 중이었고 지금 완전히 보이는 상태라면 재생을 재개한다 */
  #activate() {
    if (!this.getAttribute('data-media-loaded')) return;
    if (!this.#isInViewport || !this.#isSlideVisible) return;
    if (!this.#wasPlayingBeforeInactive || this.isPlaying) return;

    this.playMedia();
  }

  /**
   * 재생/일시정지 상태에 대한 시각적 힌트를 업데이트한다
   * @param {boolean} isPlaying - 영상이 현재 재생 중인지 여부
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * 콘텐츠를 로드한다
   * @param {boolean} [focus] - deferred-media 컨테이너에 포커스를 줄지 여부
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    /** @type {HTMLIFrameElement | null} */
    const iframe =
      content instanceof HTMLIFrameElement
        ? content
        : content instanceof HTMLElement
          ? content.querySelector('iframe[data-video-type]')
          : null;

    /** @type {HTMLVideoElement | null} */
    const video =
      content instanceof HTMLVideoElement
        ? content
        : content instanceof HTMLElement
          ? content.querySelector('video')
          : null;

    if (iframe?.dataset.videoType) {
      try {
        const url = new URL(iframe.src);

        if (iframe.dataset.videoType === 'youtube') {
          url.searchParams.set('enablejsapi', '1');
          url.searchParams.set('autoplay', '1');
          url.searchParams.set('mute', '1');
          // YouTube의 postMessage API는 origin이 일치하지 않으면 명령(pauseVideo 등)을 조용히 무시한다.
          if (!url.searchParams.has('origin')) {
            url.searchParams.set('origin', window.location.origin);
          }
        } else {
          url.searchParams.set('api', '1');
          url.searchParams.set('autoplay', '1');
          url.searchParams.set('muted', '1');
        }

        iframe.src = url.toString();
      } catch {
        // 잘못된 임베드 URL은 무시한다
      }
    }

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    const playButton = this.refs.deferredMediaPlayButton;
    if (playButton instanceof HTMLButtonElement) {
      playButton.classList.add('deferred-media__playing');
    }

    if (focus) {
      const removeTabindex = () => {
        this.removeAttribute('tabindex');
      };

      this.addEventListener('focus', removeTabindex, { once: true });
      this.setAttribute('tabindex', '-1');
      this.focus();
    }

    if (playButton instanceof HTMLButtonElement) {
      playButton.disabled = true;
    }

    if (iframe?.dataset.videoType) {
      // 핸드셰이크가 먼저 일어나도록 'load' 리스너보다 먼저 등록
      this.#syncRealPlaybackState(iframe);
      const play = () => this.playMedia();
      iframe.addEventListener('load', play, { once: true });
      play();
    } else if (video) {
      video.play().catch(() => {});
      this.#syncRealPlaybackState(video);
    }
  }

  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":[]}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.#setPlaybackState(true);
  }

  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube' ? '{"event":"command","func":"pauseVideo","args":[]}' : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.#setPlaybackState(false);
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

// youtube, vimeo 썸네일 로드
class VideoPreviewThumbnail extends HTMLElement {
  static #youtubeIdRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  static #vimeoIdRegex = /vimeo\.com\/(?:.*\/)?(?:videos\/)?([0-9]+)/;
  static #youtubeQualities = ['maxresdefault', 'sddefault', 'hqdefault', 'mqdefault'];
  static #fallbackImage = 'data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTI4MCA3MjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPHJlY3Qgd2lkdGg9IjEyODAiIGhlaWdodD0iNzIwIiBmaWxsPSIjMDAwMDAwIi8+Cjwvc3ZnPgo=';

  /** @type {HTMLImageElement | null} */
  #img = null;

  async connectedCallback() {
    const url = this.dataset.url;
    this.#img = /** @type {HTMLImageElement | null} */ (this.querySelector('.deferred-media__poster-image'));

    if (!url || !this.#img) return;

    const youtubeId = this.#getYouTubeId(url);
    if (youtubeId) {
      this.#setYouTubeThumbnail(youtubeId);
      return;
    }

    const vimeoId = this.#getVimeoId(url);
    if (vimeoId) {
      await this.#setVimeoThumbnail(vimeoId);
      return;
    }

    console.warn('Unsupported video URL:', url);
  }

  /**
   * @param {string} url
   * @returns {string | null}
   */
  #getYouTubeId(url) {
    const match = url.match(VideoPreviewThumbnail.#youtubeIdRegex);
    return match?.[1] ?? null;
  }

  /**
   * @param {string} url
   * @returns {string | null}
   */
  #getVimeoId(url) {
    const match = url.match(VideoPreviewThumbnail.#vimeoIdRegex);
    return match?.[1] ?? null;
  }

  /**
   * @param {string} videoId
   */
  #setYouTubeThumbnail(videoId) {
    const img = this.#img;
    if (!img) return;

    // YouTube는 존재하지 않는 화질(예: maxresdefault) 요청 시 404가 아니라 120x90 회색
    // placeholder 이미지를 200 OK로 반환한다. onerror가 발동하지 않으므로 로드 후 크기를
    // 확인해서 placeholder면 다음 화질로 넘어간다.
    const PLACEHOLDER_WIDTH = 120;
    const PLACEHOLDER_HEIGHT = 90;

    let i = 0;
    const tryNext = () => {
      if (i >= VideoPreviewThumbnail.#youtubeQualities.length) {
        img.onerror = null;
        img.onload = null;
        img.src = VideoPreviewThumbnail.#fallbackImage;
        return;
      }
      img.src = `https://img.youtube.com/vi/${videoId}/${VideoPreviewThumbnail.#youtubeQualities[i]}.jpg`;
      i++;
    };

    img.onerror = tryNext;
    img.onload = () => {
      if (img.naturalWidth === PLACEHOLDER_WIDTH && img.naturalHeight === PLACEHOLDER_HEIGHT) {
        tryNext();
      }
    };
    tryNext();
  }

  /**
   * @param {string} videoId
   */
  async #setVimeoThumbnail(videoId) {
    const img = this.#img;
    if (!img) return;

    try {
      const res = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`);
      if (!res.ok) throw new Error('oEmbed fetch failed');

      const data = await res.json();
      img.src = data.thumbnail_url.replace(/_\d+\.jpg$/, '_1280.jpg');
    } catch (err) {
      console.error('Vimeo thumbnail error:', err);
      img.src = VideoPreviewThumbnail.#fallbackImage;
    }
  }
}

if (!customElements.get('video-preview-thumbnail')) {
  customElements.define('video-preview-thumbnail', VideoPreviewThumbnail);
}
